// ---------------------------------------------------------------------------
// Expanding a print run into one record per sticker.
//
// Serials are owned here rather than by the API: a run is described by its field
// values plus a serial range, and uniqueness comes from the pair
// (production/batch, serial) — so a reprint of the same range reproduces exactly
// the same labels.
// ---------------------------------------------------------------------------

import { placeholdersIn } from "./zplRenderer";

/** Fields that come from the saved template and must not be edited at print time. */
export const LOCKED_FIELDS = ["barcode", "productName"];

/** Fields the run generates per sticker rather than asking the operator for. */
export const GENERATED_FIELDS = ["serial", "index", "total"];

/** Every placeholder the template actually uses, de-duplicated, in element order. */
export function templatePlaceholders(label) {
  const out = [];
  (label.elements || []).forEach((el) => {
    placeholdersIn(el.text).forEach((p) => {
      if (out.indexOf(p) === -1) out.push(p);
    });
  });
  return out;
}

/** Placeholders the operator is asked to fill in before printing. */
export function editablePlaceholders(label) {
  return templatePlaceholders(label).filter(
    (p) => LOCKED_FIELDS.indexOf(p) === -1 && GENERATED_FIELDS.indexOf(p) === -1
  );
}

export function formatSerial(n, pad) {
  const s = String(n);
  return pad > 0 ? s.padStart(pad, "0") : s;
}

/**
 * Expand a run into one record per sticker.
 *
 * @param {object} fields       values keyed by placeholder name
 * @param {object} serials      { start, count, pad }
 */
export function buildRecords(fields, serials) {
  const start = Math.max(0, Math.round(serials.start || 0));
  const count = Math.max(0, Math.round(serials.count || 0));
  const pad = Math.max(0, Math.round(serials.pad || 0));

  const out = [];
  for (let i = 0; i < count; i++) {
    out.push({
      ...fields,
      serial: formatSerial(start + i, pad),
      serialNumber: start + i,
      index: i + 1,
      total: count,
    });
  }
  return out;
}

/** Where the next run should start, so a follow-up run does not reuse serials. */
export function nextSerialStart(serials) {
  return Math.max(0, Math.round(serials.start || 0)) + Math.max(0, Math.round(serials.count || 0));
}
